import { useInView } from "react-intersection-observer";

import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useMutation } from "@apollo/react-hooks";
import { FieldError } from "@streeteasy/design-system/form";
import { bool } from "prop-types";

import GlobalState from "src/shared/utilities/globalState";

import SAVE from "../../mutations/save";
import { recentSearchProps } from "../../propTypes";
import buildRecentSearchesImpressionLabel from "../../utilities/buildRecentSearchesImpressionLabel";
import * as S from "./styled";

const RecentSearchItem = ({ isSaved, recentSearch }) => {
  const { userId } = useSelector(state => state.userId);
  const [isSavedSearch, toggleIsSavedSearch] = useState(isSaved);
  const [searchUrl, setSearchUrl] = useState(recentSearch.url);
  const [toggleSave, { error }] = useMutation(SAVE, {
    variables: {
      item_id: recentSearch.id,
      item_type: "Search",
      toggle_save: !!userId
    }
  });

  const { inView, ref } = useInView({
    triggerOnce: true,
    threshold: 0.75
  });

  useEffect(() => {
    setSearchUrl(url => {
      const newUrl = new URL(url);
      if (isSaved) {
        newUrl.searchParams.append("saved_search", 1);
      } else {
        newUrl.searchParams.append("recent_search", 1);
      }

      return newUrl.href;
    });
  }, []);

  useEffect(() => {
    if (inView) {
      window.dataLayer.push({
        event: "custom_event",
        event_category: "recent_searches",
        event_action: "impression",
        event_label: buildRecentSearchesImpressionLabel([recentSearch])
      });
    }
  }, [inView]);

  const analyticsSaveSearch = () => {
    window.dataLayer.push({
      event: "custom_event",
      event_category: "save",
      event_action: "search",
      event_label: recentSearch.id
    });
  };

  const toggleSaveCallback = () => {
    toggleSave().then(() => {
      window.location.reload();

      analyticsSaveSearch();
    });
  };

  const handleSaveClick = () => {
    if (!userId) {
      const triggerScenario = GlobalState.get("triggerScenario");

      if (typeof triggerScenario === "function") {
        GlobalState.get("callbacks").accountCreated = undefined;
        GlobalState.get("callbacks").login = undefined;

        GlobalState.registerCallback("accountCreated", toggleSaveCallback);
        GlobalState.registerCallback("login", toggleSaveCallback);

        triggerScenario("EmailCapture");
      }
    } else {
      toggleIsSavedSearch(previousState => {
        if (!previousState) analyticsSaveSearch();

        setSearchUrl(url => {
          const newUrl = new URL(url);
          if (previousState) {
            newUrl.searchParams.append("recent_search", 1);
            newUrl.searchParams.delete("saved_search");
          } else {
            newUrl.searchParams.append("saved_search", 1);
            newUrl.searchParams.delete("recent_search");
          }

          return newUrl.href;
        });

        return !previousState;
      });

      toggleSave();
      sessionStorage.setItem("isSavedSearch", true);
    }

    GlobalState.set("analyticsData", { source: "nav_recent_searches" });
  };

  return (
    <S.Container ref={ref}>
      <S.Link href={searchUrl}>{recentSearch.title}</S.Link>
      <img alt="" aria-hidden src={recentSearch.areas[0].staticMapUrl} />
      <S.RecentSearchContent>
        <S.Title>{recentSearch.label || recentSearch.title}</S.Title>
        <S.Description>{recentSearch.titleForSearch}</S.Description>
        <S.Button
          aria-label={
            isSavedSearch ? "Remove from saved searches" : "Save Search"
          }
          disabled={error}
          onClick={handleSaveClick}
        >
          {error ? (
            <FieldError>Something went wrong</FieldError>
          ) : (
            <S.SaveIcon iconClass={isSavedSearch ? "heart" : "heart-o"} />
          )}
        </S.Button>
      </S.RecentSearchContent>
    </S.Container>
  );
};

RecentSearchItem.propTypes = {
  isSaved: bool.isRequired,
  recentSearch: recentSearchProps.isRequired
};

export default RecentSearchItem;
